import AddIcon from "@mui/icons-material/Add";
import { styled } from "@mui/material/styles";
import { ButtonProps } from "designSystem/components/button/Button";
import { Box, Button, DataGrid, DataGridCardProps, FlexColumn, FlexRowCenter, FormControlLabel, GalleryPlaceholder, Grid, GridItemGrow, InfoIconButton, SaveDialog, SearchTextField, Switch, Text, ToolbarSeparator } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { STANDARD_BORDER_STYLE } from "designSystem/styles/style-functions";
import DataStoreSelectionStore from "csi/datastore/DataStoreSelectionStore";
import FilterPillGroup from "csi/middleware/filter/FilterPillGroup";
import { useStore } from "csi/state/hooks";
import { C8Node } from "csi/typings";
import _ from "lodash";
import { action } from "mobx";
import { observer } from "mobx-react-lite";
import React, { ChangeEventHandler, Fragment, FunctionComponent, useState } from "react";
import PPProductCard, { COLUMNS_BY_WIDTH, PPProductCardSkeleton } from "./NewProductPresentation/PPProductCard";
import { ProductGalleryStoreContext } from "./NewProductPresentation/ProductGalleryStore";
import PPAttributeSetting from "./PPAttributeSetting";
import PPFilterPane from "./PPFilterPane";
import PPProductLayoutStore, { PPProductLayoutStoreContext, usePPProductLayoutStore } from "./PPProductLayoutStore";
import { isPresentationEditor } from "./PPService";
import SetEditForm from "./SetEditForm";
import { SetEditStoreContext } from "./store/SetEditStore";

const PREFIX = "PPProductLayout";

const classes = {
	galleryPane: `${PREFIX}galleryPane`,
	negateTabMargins: `${PREFIX}negateTabMargins`,
	rightPane: `${PREFIX}rightPane`,
	root: `${PREFIX}root`,
	tabMargin: `${PREFIX}tabMargin`,
};

const StyledBox = styled(Box)((
	{
		theme,
	},
) => ({
	[`& .${classes.galleryPane}`]: {
		flexGrow: 1,
		marginTop: theme.spacing(3),
		overflow: "auto",
		padding: theme.spacing(4),
		paddingTop: 0,
	},

	[`& .${classes.negateTabMargins}`]: {
		marginLeft: -8,
		marginRight: -16,
		marginTop: -12,
	},

	[`& .${classes.rightPane}`]: {
		borderLeft: STANDARD_BORDER_STYLE,
		flexShrink: 0,
		width: 320,
	},

	[`&.${classes.root}`]: {
		display: "flex",
		flexWrap: "nowrap",
		height: "100%",
		width: "100%",
	},

	[`&.${classes.tabMargin}`]: {
		paddingLeft: theme.spacing(2),
	},
}));

const PPAddButton: FunctionComponent<ButtonProps> = observer((props) => {
	const loc = useLocale();
	const rootStore = useStore();
	const productStore = usePPProductLayoutStore();
	const { presentation, primaryType, saveProducts } = productStore;
	const [addProductStore, setAddProductStore] = useState<PPProductLayoutStore>(null);

	const onAdd = () => {
		const selectionStore = new DataStoreSelectionStore({ multiple: true });
		const addProductStore = new PPProductLayoutStore(rootStore, presentation, selectionStore, loc);
		setAddProductStore(addProductStore);
	};

	const onClose = () => {
		setAddProductStore(null);
	};

	const onSave = async () => {
		await saveProducts(addProductStore.selectionStore.addedSelections);
		return true;
	};

	return (
		<Fragment>
			<Button
				onClick={onAdd}
				res="PPAddProduct"
				resParams={[loc.type(primaryType, true)]}
				startIcon={<AddIcon />}
				{...props}
			/>
			{addProductStore && (
				<SaveDialog
					confirmClose={addProductStore.selectionStore.hasChanged}
					contentSpacing={0}
					disableSave={!addProductStore.selectionStore.hasChanged}
					fullHeight
					fullWidth
					onClose={onClose}
					onSave={onSave}
					open
					scrollContent="belowMessage"
					titleRes="PPAddProductTitle"
					titleResParams={[loc.type(primaryType, true), presentation.$Name]}
				>
					{{
						main: <PPProductLayout productStore={addProductStore} />,
					}}
				</SaveDialog>
			)}
		</Fragment>
	);
});

const PPLayoutMessaage = observer(() => {
	const loc = useLocale();
	const { primaryType, resultCount, selectionStore } = usePPProductLayoutStore();

	return (
		<Text
			bold
			color="textSecondary"
			res={selectionStore ? "PPProductsMatchRules" : "ShowResults"}
			resParams={[resultCount, loc.type(primaryType, resultCount !== 1)]}
		/>
	);
});

const PPToolbar = observer(() => {
	const { localStore: { productPresentation } } = useStore();
	const { showAttributes } = productPresentation;
	const {
		primaryType,
		resultCount,
		resultCountLimit,
		resultCountOverLimit,
		selectAll,
		selectionStore,
		store,
		setSearchText,
	} = usePPProductLayoutStore();

	const toggleShowAttributes = action(() => {
		productPresentation.showAttributes = !showAttributes;
	});

	const resParams = [useLocale().type(primaryType, true), resultCountLimit];

	const onChangeText: ChangeEventHandler = _.debounce((event) => {
		setSearchText(event.target.value);
	}, 1000);

	return (
		<Fragment>
			<FlexRowCenter mt={4} mx={4}>
				<Grid
					alignItems="center"
					container
					spacing={2}
				>
					{!selectionStore && isPresentationEditor() && (
						<Grid item>
							<PPAddButton design="primary" />
						</Grid>
					)}
					<Grid item>
						<SearchTextField
							autoFocus
							onChange={onChangeText}
							placeholderRes="Search"
						/>
					</Grid>
					{selectionStore && !!resultCount && (
						<Fragment>
							<Grid item>
								<Button
									design="link"
									disabled={resultCountOverLimit}
									onClick={selectAll}
									res="SelectAllResults"
								/>
							</Grid>
							<Grid item>
								<ToolbarSeparator />
							</Grid>
							<Grid item>
								<Button
									design="link"
									disabled={!selectionStore.hasSelections}
									onClick={selectionStore.clearSelections}
									res="UnselectAllResults"
								/>
							</Grid>
							{resultCountOverLimit && (
								<InfoIconButton
									className={null}
									res="PPSelectAllLimit"
									resParams={resParams}
								/>
							)}
						</Fragment>
					)}
					<GridItemGrow />
					{store && !store.isLoading && (
						<Grid item>
							<PPLayoutMessaage />
						</Grid>
					)}
					<Grid item>
						<ToolbarSeparator />
					</Grid>
					<Grid item>
						<FormControlLabel
							control={(
								<Switch
									checked={showAttributes}
									onChange={() => toggleShowAttributes()}
								/>
							)}
							labelPlacement="start"
							res="DetailView"
						/>
					</Grid>
				</Grid>
			</FlexRowCenter>
		</Fragment>
	);
});

const PPProductPlaceholder = () => {
	const loc = useLocale();
	const {
		primaryType,
		selectionStore,
		showActions,
	} = usePPProductLayoutStore();
	const resParams = [loc.type(primaryType, true)];

	return (
		<GalleryPlaceholder
			height={1}
			subtitleMargin={6}
			subtitleProps={selectionStore ? null : { res: "PPAddProductPlaceholderSubtitle", resParams }}
			titleProps={selectionStore ? null : { res: "PPAddProductPlaceholder", resParams }}
		>
			{showActions ? { actions: <PPAddButton design="secondary" /> } : null}
		</GalleryPlaceholder>
	);
};

const PPProductCardForEditRule: FunctionComponent<DataGridCardProps<C8Node>> = observer((props) => {
	const { selectionStore } = usePPProductLayoutStore();
	const hideUsedInSection = !!selectionStore;

	return <PPProductCard hideUsedInSection={hideUsedInSection} {...props} />;
});

const PPGallery = observer(() => {
	const { galleryStore, selectionStore, store } = usePPProductLayoutStore();

	return (
		<ProductGalleryStoreContext.Provider value={galleryStore}>
			<DataGrid
				CardComponent={PPProductCardForEditRule}
				PlaceholderComponent={PPProductPlaceholder}
				SkeletonComponent={PPProductCardSkeleton}
				columnsByWidth={COLUMNS_BY_WIDTH}
				selectionStore={selectionStore}
				spacing={3}
				store={store}
			/>
		</ProductGalleryStoreContext.Provider>
	);
});

interface PPProductLayoutProps {
	/** indicates if layout showing in tab */
	inTab?: boolean;

	/** store managing layout */
	productStore: PPProductLayoutStore;
}

const PPProductLayout: FunctionComponent<PPProductLayoutProps> = observer((props) => {
	const { inTab, productStore } = props;
	const { attributeSettingStore, fixedEditSetStore, filterStore } = productStore;
	const { galleryPane, rightPane, root, tabMargin } = classes;

	return (
		<PPProductLayoutStoreContext.Provider value={productStore}>
			<StyledBox className={`${root} ${inTab ? tabMargin : ""}`}>
				<PPFilterPane
					SettingComponent={PPAttributeSetting}
					settingComponentStore={attributeSettingStore}
				/>
				<FlexColumn flexGrow={1}>
					<PPToolbar />
					<Box className={galleryPane}>
						<FilterPillGroup store={filterStore} />
						<PPGallery />
					</Box>
				</FlexColumn>
				{fixedEditSetStore && (
					<SetEditStoreContext.Provider value={fixedEditSetStore}>
						<SetEditForm className={rightPane} />
					</SetEditStoreContext.Provider>
				)}
			</StyledBox>
		</PPProductLayoutStoreContext.Provider>
	);
});

export default PPProductLayout;